import clsx from "clsx";
import React, { ReactNode, useEffect, useRef } from "react";

import { FieldLabel } from "./FieldLabel";

type CheckboxFieldProps = {
  labelClassName?: string;
  className?: string;
  label?: ReactNode;
  name?: string;
  onChange?: (value: boolean) => void;
  value?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  indeterminate?: boolean;
  error?: string;
};

export const CheckboxField = ({
  labelClassName,
  className,
  label,
  name,
  onChange = () => {},
  value,
  readOnly,
  disabled,
  indeterminate,
  error,
}: CheckboxFieldProps) => {
  const checkboxRef = useRef<HTMLInputElement>(null);
  const isDisabled = readOnly || disabled;

  const onChangeInternal = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked);
  };

  // Handle indeterminate state (can't be set via HTML attribute)
  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate || false;
    }
  }, [indeterminate]);

  return (
    <FieldLabel
      label={label}
      className={clsx("flex", labelClassName)}
      error={error}
    >
      <input
        ref={checkboxRef}
        type="checkbox"
        className={clsx(
          "!w-3 !h-3 rounded",
          "transition-all duration-200",
          "outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 dark:focus:ring-offset-gray-900",
          "border-2",
          {
            "cursor-not-allowed opacity-50": isDisabled,
            "cursor-pointer": !isDisabled,
            "border-gray-300 dark:border-gray-600": !error && !value && !indeterminate,
            "border-error dark:border-error-light": error,
            "border-brand bg-brand": (value || indeterminate) && !error,
            "border-error bg-error": value && error,
          },
          "hover:border-brand dark:hover:border-brand-light",
          className,
        )}
        onChange={onChangeInternal}
        name={name}
        checked={value}
        disabled={isDisabled}
      />
    </FieldLabel>
  );
};
